<template>
  <div class="el-pagination">
    <button :disabled="isFirstPage" class="pagination-button arrow" @click="selectPage(currentPage - 1)">
      &laquo;
    </button>
    <template v-for="pageNum in totalPages">
      <button v-if="shouldDisplayPage(pageNum)" :key="pageNum" :class="['pagination-button', { 'active': pageNum === currentPage }]" @click="selectPage(pageNum)">
        {{ pageNum }}
      </button>
      <span v-if="shouldDisplayEllipsis(pageNum)" :key="'ellipsis-' + pageNum" class="ellipsis">
        &hellip;
      </span>
    </template>
    <button :disabled="isLastPage" class="pagination-button arrow" @click="selectPage(currentPage + 1)">
      &raquo;
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits } from 'vue';
import {PaginationData} from '~/@types/common';

// PROPS Definitions
const props = defineProps<{
  paginationData: PaginationData;
}>();

// EMIT Definitions
const emit = defineEmits<{
  (event: 'pageChanged', payload: any): any;
}>();

// DATA Definitions
const currentPage = ref(props.paginationData.page);
const isFirstPage = computed(() => currentPage.value === 1);
const isLastPage = computed(() => currentPage.value === totalPages.value.length);

/**
 * @_WATCH pagination data page
 */
watch(() => props.paginationData.page, () => {
  if (currentPage.value !== props.paginationData.page) {
    currentPage.value = props.paginationData.page;
  }
});

/**
 * Get total pages
 */
const totalPages = computed(() => {
  const total = Math.ceil(props.paginationData.totalRecords / props.paginationData.perPage);
  return total > 0 ? Array.from({ length: total }, (_, i) => i + 1) : [];
});

/**
 * Set Selected page
 * @param pageNum
 */
const selectPage = (pageNum: number) => {
  if (pageNum === currentPage.value) {
    return;
  }
  currentPage.value = pageNum;
  emit('pageChanged', pageNum);
};

/**
 * Should display page numbers
 *
 * @param pageNum
 */
const shouldDisplayPage = (pageNum: number) => {
  const total = totalPages.value.length;

  if (total <= 8) return true;

  return (
    pageNum <= 3 ||
    pageNum > total - 3 ||
    (pageNum >= currentPage.value - 1 && pageNum <= currentPage.value + 1)
  );
};

/**
 * Should display Ellipsis (dots ...)
 * @param pageNum
 */
const shouldDisplayEllipsis = (pageNum: number) => {
  const total = totalPages.value.length;

  if (total <= 8) return false;

  return (
    (pageNum === 4 && currentPage.value > 4) ||
    (pageNum === total - 3 && currentPage.value < total - 3)
  );
};
</script>

<style scoped lang="scss">
.el-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Arial, sans-serif;
  padding: 8px 8px 30px;
}

.pagination-button {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #fff;
  border: 1px solid transparent;
  color: #333;
  font-size: 14px;
  margin: 0 3px;
  transition: all 0.3s;

  &:hover:not([disabled]) {
    background-color: rgb(var(--primary));
    border-color: rgb(var(--primary-dark));
    color: #fff;
    transform: scale(1.1);
  }

  &.active {
    background-color: rgb(var(--primary));
    border-color: rgb(var(--primary-dark));
    color: #fff;
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.arrow {
  font-size: 1.1em;
}
</style>
